import { find } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import SelectField from '@/components/select-field'
import { Button, Card, Checkbox } from '@/components/ui'
import { Plus, Trash } from 'lucide-react'
import Loader from '../../../common/components/Loader'
import { Partner, PartnerCategory } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { useTableParams } from '../../../core/hooks/table-params'
import {
  deletePartner,
  getPartnerCategories,
  getPartners,
  getPartnersByCategory,
  publishPartner,
  unpublishPartner,
} from '../../../core/services/partners'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { CreatePartnerListModal } from './CreatePartnerListModal'
import { ActionsMenu } from '@/components/ActionsMenu'
import { Icon } from '@/common/components/Icon'

export const PartnerListPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
  } = useTableParams()

  const { festivalName } = useParams()

  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)

  const [showCreatePartnerModal, setShowCreatePartnerModal] =
    useState<boolean>(false)
  const [editMode, setEditMode] = useState<boolean>(false)
  const [currentPartner, setCurrentPartner] = useState<Partner>()
  const [partners, setPartners] = useState<Partner[]>([])
  const [partnerCategories, setPartnerCategories] = useState<PartnerCategory[]>(
    [],
  )
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)
  const [selectedCategory, setSelectedCategory] = useState<
    PartnerCategory | undefined
  >(undefined)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      if (selectedCategory) {
        const response = await getPartnersByCategory(selectedCategory.id, {
          page,
          size,
          sortBy,
          sortDirection,
        })
        setPartners(response.data.content)
        setTotalElemenets(response.data.totalElements)
      } else {
        const response = await getPartners({
          page,
          size,
          sortBy,
          sortDirection,
        })
        setPartners(response.data.content)
        setTotalElemenets(response.data.totalElements)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [selectedCategory, page, size, sortBy, sortDirection, setTotalElemenets])

  useEffect(() => {
    getData()
  }, [getData])

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const response = await getPartnerCategories({
          size: 1000,
        })
        setPartnerCategories(response.data.content)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }, [festivalName])

  const handleSelectAll = useCallback(() => {
    const nextChecked = !isCheckAll
    setIsCheckAll(nextChecked)
    setIsCheck(nextChecked ? partners.map((partner) => partner.id) : [])
  }, [isCheckAll, partners])

  const handleClick = useCallback((id: string, checked: boolean) => {
    setIsCheck((prev) =>
      checked ? [...prev, id] : prev.filter((item) => item !== id),
    )
  }, [])

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  const handleDelete = () => {
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const deleteFunc = async () => {
    try {
      const { status } = await deletePartner(isCheck)
      getData()
      setIsCheck([])
      if (status === 204) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Delete Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const handlePublish = useCallback(
    async (checkedIds: string[]) => {
      try {
        setLoading(true)
        const { status } = await publishPartner(checkedIds)
        getData()
        setIsCheck([])
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Publish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    },
    [getData],
  )

  const handleUnpublish = useCallback(
    async (checkedIds: string[]) => {
      try {
        setLoading(true)
        const { status } = await unpublishPartner(checkedIds)
        getData()
        setIsCheck([])
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Unpublish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    },
    [getData],
  )

  const pageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const categoryOptions = useMemo(
    () => [
      { value: 'all', label: 'Partner Category' },
      ...partnerCategories.map((partnerCategory) => ({
        value: partnerCategory.id,
        label: partnerCategory.name,
      })),
    ],
    [partnerCategories],
  )

  const handleCategoryChange = useCallback(
    (value: string) => {
      if (value === 'all') {
        setSelectedCategory(undefined)
        return
      }
      setSelectedCategory(find(partnerCategories, (cat) => cat.id === value))
    },
    [partnerCategories],
  )

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: (
          <Checkbox checked={isCheckAll} onCheckedChange={handleSelectAll} />
        ),
        accessor: '',
        render: (row) => (
          <Checkbox
            checked={isCheck.includes(row.id)}
            onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          />
        ),
        width: '16px',
      },
      {
        title: (
          <SelectField
            options={categoryOptions}
            outline
            value={selectedCategory?.id ?? 'all'}
            onValueChange={handleCategoryChange}
            placeholder="Partner Category"
          />
        ),
        accessor: '',
        render: (row) => row.partnerCategory.name,
      },
      {
        title: 'Name',
        accessor: 'name',
        sortable: true,
      },
      {
        title: 'Logo',
        accessor: 'imageLinkMd',
        sortable: false,
        render: (row) => (
          <img
            className="h-10 w-10 rounded-[10px] object-contain"
            src={row.imageLinkMd}
          />
        ),
      },
      {
        title: 'Priority',
        accessor: 'priority',
        sortable: true,
      },
      {
        title: 'Published',
        accessor: '',
        render: (row) => (
          <Checkbox
            checked={row.published}
            onCheckedChange={() => {
              if (row.published) {
                handleUnpublish([row.id])
              } else {
                handlePublish([row.id])
              }
            }}
          />
        ),
      },
      {
        title: 'Edit',
        accessor: '',
        render: (row) => (
          <button
            type="button"
            className="view text-accent-1 underline"
            onClick={() => {
              setEditMode(true)
              setCurrentPartner(row)
              setShowCreatePartnerModal(true)
            }}
          >
            Edit
          </button>
        ),
      },
    ],
    [
      categoryOptions,
      handleCategoryChange,
      handleClick,
      handlePublish,
      handleSelectAll,
      handleUnpublish,
      isCheck,
      isCheckAll,
      selectedCategory,
    ],
  )

  return (
    <>
      <Header
        pageTitle="Partners"
        actions={
          <>
            {isCheck.length >= 1 && (
              <>
                <ActionsMenu
                  actions={[
                    { label: 'Publish', onClick: () => handlePublish(isCheck) },
                    {
                      label: 'Unpublish',
                      onClick: () => handleUnpublish(isCheck),
                    },
                  ]}
                />
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                  Delete
                </Button>
              </>
            )}
            <Button
              variant="primary-bordered"
              onClick={() => {
                setEditMode(false)
                setShowCreatePartnerModal(true)
                setCurrentPartner(undefined)
              }}
            >
              Add New
              <Icon
                icon="plus-circle"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              />
            </Button>
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Partners" subtitle="Partners List" className="mb-5" />
        <Table
          columnWidth="30%"
          isCheck={isCheck}
          columns={columns}
          handleSortBy={handleSortBy}
          data={partners}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={pageChange}
      />
      <CreatePartnerListModal
        setEditMode={setEditMode}
        setShowCreateModal={setShowCreatePartnerModal}
        partner={currentPartner}
        setPartners={setPartners}
        categories={partnerCategories}
        showCreateModal={showCreatePartnerModal}
        editMode={editMode}
        page={page + 1}
        size={size}
        setTotalElemenets={setTotalElemenets}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Partner'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
